import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Settings as SettingsIcon, Mail, Sparkles, Database, FileText, FileSignature, Building2, BookOpen, Key, Link2, Shield, Globe, Webhook, AtSign, Activity, DollarSign, Zap, Network } from 'lucide-react';
import { usePendingApprovalCounts } from '@/lib/usePendingApprovalCounts';
import { useSystemCapabilities } from '@/lib/systemCapabilities';

export default function Settings() {
  const location = useLocation();
  const counts = usePendingApprovalCounts();
  const caps = useSystemCapabilities();

  // If at /settings, redirect to /settings/email by default
  if (location.pathname === '/settings') {
    window.location.href = '/settings/email';
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6">
      {/* Side Menu — full width på mobil, sidebar på desktop */}
      <div className="w-full md:w-64 md:flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
            <SettingsIcon size={20} />
            Systeminnstillinger
          </h2>
          <nav className="grid grid-cols-2 md:grid-cols-1 gap-1 md:space-y-1">
            <SideNavLink to="/settings/email" icon={<Mail size={18} />}>
              E-postinnstillinger
            </SideNavLink>
            <SideNavLink to="/settings/ai-prompts" icon={<Sparkles size={18} />}>
              KI-prompts
            </SideNavLink>
            <SideNavLink to="/settings/api" icon={<Key size={18} />}>
              API-integrasjoner
            </SideNavLink>
            <SideNavLink to="/settings/api-access" icon={<Webhook size={18} />}>
              API-tilganger (V1)
            </SideNavLink>
            <SideNavLink to="/settings/integrations" icon={<Link2 size={18} />}>
              LMS-integrasjoner
            </SideNavLink>
            <SideNavLink to="/settings/email-domains" icon={<AtSign size={18} />} badge={counts.domains}>
              E-postdomener
            </SideNavLink>
            <SideNavLink to="/settings/member-admin-requests" icon={<Shield size={18} />} badge={counts.adminRequests}>
              Admin-forespørsler
            </SideNavLink>
            <SideNavLink to="/settings/legal" icon={<FileText size={18} />}>
              Juridiske tekster
            </SideNavLink>
            <SideNavLink to="/settings/dpa" icon={<FileSignature size={18} />}>
              Databehandleravtaler
            </SideNavLink>
            <SideNavLink to="/settings/subprocessors" icon={<Network size={18} />}>
              Underdatabehandlere
            </SideNavLink>
            <SideNavLink to="/settings/backup" icon={<Database size={18} />}>
              Sikkerhetskopier
            </SideNavLink>
            <SideNavLink to="/settings/esco" icon={<Globe size={18} />}>
              ESCO Taksonomi
            </SideNavLink>
            <SideNavLink to="/settings/activity-log" icon={<Activity size={18} />}>
              Aktivitetslogg
            </SideNavLink>
            <SideNavLink to="/settings/token-usage" icon={<DollarSign size={18} />}>
              KI-tokenforbruk
            </SideNavLink>
            {caps?.composite_assessment && (
              <>
                <SideNavLink to="/settings/ai-quotas" icon={<Zap size={18} />}>
                  KI-kvoter og fakturering
                </SideNavLink>
                <SideNavLink to="/settings/rubrics" icon={<FileText size={18} />}>
                  Vurderingsmatriser
                </SideNavLink>
              </>
            )}
            <SideNavLink to="/settings/devices" icon={<Shield size={18} />}>
              Mine enheter
            </SideNavLink>
            <SideNavLink to="/settings/account" icon={<Building2 size={18} />}>
              Konto
            </SideNavLink>
            <SideNavLink to="/settings/documentation" icon={<BookOpen size={18} />}>
              Dokumentasjon
            </SideNavLink>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <Outlet />
      </div>
    </div>
  );
}

function SideNavLink({ to, icon, badge, children }: { to: string; icon: React.ReactNode; badge?: number; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-kf-primary text-white'
            : 'text-gray-700 hover:bg-gray-100'
        }`
      }
    >
      {icon}
      <span className="flex-1">{children}</span>
      {badge !== undefined && badge > 0 && (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
          {badge}
        </span>
      )}
    </NavLink>
  );
}
